import BigNumber from 'bignumber.js'
import { ChainIdEnum } from 'config/constants/network'
import tokens from 'config/constants/tokens'
import {
  BonusTagEnums,
  ICommonBonus,
  IWageringCondition,
  IWageringConditionBigAmount,
  MaxCashoutTypeEnum,
} from 'config/types/bonus'
import { RowBetween } from 'layout/Components/Row'
import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useTokenUsdPrices } from 'state/app/hooks'
import styled from 'styled-components'
import { Icons } from 'svgs'
import { colors } from 'theme/colors'
import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import { FlexProps } from 'UIKit/Box/types'
import Text from 'UIKit/Text/Text'
import { buildPluralizeText } from 'utils'
import {
  estimateFiatDecimalValues,
  formatDisplayEstimateFiat,
  isFreeSpinBonusFiatReward,
  isWagerBonusFiatReward,
} from 'utils/bonus'
import { getFullDisplayBalance } from 'utils/formatBalance'
import BonusDetailStepCard from '../BonusDetailStepCard'

interface ActivationInfoSectionProps {
  bonus: IWageringCondition & IWageringConditionBigAmount & ICommonBonus
  step: number
  isActive: boolean
  isProcessing: boolean
}

const ActivationInfoSection: React.FC<ActivationInfoSectionProps & FlexProps> = ({
  bonus,
  step,
  isActive,
  isProcessing,
  ...props
}) => {
  const { t } = useTranslation()
  const tokenUsdPrices = useTokenUsdPrices()
  const bonusAmountValue = Number(getFullDisplayBalance(bonus.bonusAmount.amount))
  const isMaxCashoutMultiplierBonusValue = bonus.maxCashoutType === MaxCashoutTypeEnum.MultiplierBonusValue
  const isMaxCashoutFixedValue = bonus.maxCashoutType === MaxCashoutTypeEnum.FixedValue

  const maxCashoutDisplayValue =
    isMaxCashoutMultiplierBonusValue && bonusAmountValue > 0
      ? new BigNumber(Number(bonus.maxCashoutInUsd) * bonusAmountValue).times(
          new BigNumber(tokenUsdPrices[bonus.bonusAmount.token.code]),
        )
      : bonus.maxCashoutInUsdBigAmount

  const maxCashoutDisplayFullContent = isMaxCashoutFixedValue
    ? `$${getFullDisplayBalance(maxCashoutDisplayValue, 0, 2)}`
    : bonusAmountValue
    ? `(~$${getFullDisplayBalance(maxCashoutDisplayValue, 0, 2)})`
    : ''

  return (
    <BonusDetailStepCard
      {...props}
      isActive={isActive}
      isProcessing={isProcessing}
      cardTitle={
        <Flex flexDirection="column">
          <Text
            textTransform="uppercase"
            fontSize="10px"
            fontWeight="600"
            ml="6px"
            lineHeight="normal"
            color="textAlt1"
          >
            <Trans>Step {{ step }}</Trans>
          </Text>
          <Text fontSize="14px" fontWeight="600" ml="6px" lineHeight="22px">
            <Trans>Activation</Trans>
          </Text>
        </Flex>
      }
    >
      <Box borderRadius="8px" ml="6px" mt="12px">
        <RowBetween mb="16px">
          <Text fontSize="14px" fontWeight="300" color="textSubtle">
            <Trans>Min Deposit</Trans>
          </Text>
          <Text fontSize="14px" fontWeight="600" color="textTertiary">
            {bonus?.tags?.includes(BonusTagEnums.NoDeposit) ? (
              <Trans>No deposit</Trans>
            ) : (
              <span>
                ${getFullDisplayBalance(bonus.minDepositBigAmount)}{' '}
                <StyledFiatEstimated>
                  {isFreeSpinBonusFiatReward(bonus) &&
                    (bonus as any).freeSpinCurrency !== 'USD' &&
                    formatDisplayEstimateFiat(
                      bonus.minDepositBigAmount
                        .dividedBy(tokenUsdPrices[(bonus as any).freeSpinCurrency])
                        .toFixed(
                          estimateFiatDecimalValues(tokens[ChainIdEnum.FIAT][(bonus as any)?.freeSpincurrency]),
                          BigNumber.ROUND_UP,
                        )
                        .toString(),
                      (bonus as any).freeSpinCurrency,
                    )}
                  {isWagerBonusFiatReward(bonus) &&
                    formatDisplayEstimateFiat(
                      bonus.minDepositBigAmount
                        .dividedBy(tokenUsdPrices[bonus.bonusAmount.token.code])
                        .toFixed(
                          estimateFiatDecimalValues(tokens[ChainIdEnum.FIAT][bonus.bonusAmount.token.code]),
                          BigNumber.ROUND_UP,
                        )
                        .toString(),

                      bonus.bonusAmount.token.code,
                    )}
                </StyledFiatEstimated>
              </span>
            )}
          </Text>
        </RowBetween>
        {bonus.maxBonusAmountInUsd && (
          <RowBetween mb="16px">
            <Flex>
              <Text fontSize="14px" fontWeight="300" color="textSubtle">
                <Trans>Max Bonus</Trans>
              </Text>
              <Box
                data-tooltip-id="common-tip-bottom"
                data-tooltip-content={t('Max amount of rewards you can receive')}
                ml={2}
                height="16px"
              >
                <Icons.InfoCircleIcon width="16px" fill={colors.textDisable} />
              </Box>
            </Flex>
            <Text fontSize="14px" fontWeight="600" color="textTertiary">
              <span>
                ${getFullDisplayBalance(bonus.maxBonusAmountInUsdBigAmount)}{' '}
                <StyledFiatEstimated>
                  {isFreeSpinBonusFiatReward(bonus)
                    ? formatDisplayEstimateFiat(
                        getFullDisplayBalance(
                          bonus.maxBonusAmountInUsdBigAmount.dividedBy(tokenUsdPrices[(bonus as any).freeSpinCurrency]),
                          0,
                        ),
                        (bonus as any).freeSpinCurrency,
                      )
                    : isWagerBonusFiatReward(bonus)
                    ? formatDisplayEstimateFiat(
                        getFullDisplayBalance(
                          bonus.maxBonusAmountInUsdBigAmount.dividedBy(tokenUsdPrices[bonus.bonusAmount.token.code]),
                          0,
                        ),
                        bonus.bonusAmount.token.code,
                      )
                    : ''}
                </StyledFiatEstimated>
              </span>
            </Text>
          </RowBetween>
        )}

        {bonus.maxCashoutInUsd && bonus.maxCashoutInUsdBigAmount.isGreaterThan(0) && (
          <RowBetween mb="16px">
            <Flex>
              <Text fontSize="14px" fontWeight="300" color="textSubtle">
                <Trans>Max Cashout</Trans>
              </Text>
            </Flex>
            <Text fontSize="14px" fontWeight="600" color="textTertiary">
              <span>
                {bonus.maxCashoutInUsd &&
                  isMaxCashoutMultiplierBonusValue &&
                  t('{{value}}X Bonus Value', { value: bonus.maxCashoutInUsd })}
                <StyledMaxCashoutValue $isMultiplierBonusValue={isMaxCashoutMultiplierBonusValue}>
                  {maxCashoutDisplayFullContent}
                  <StyledFiatEstimated>
                    {isFreeSpinBonusFiatReward(bonus) && (bonus as any).freeSpinCurrency !== 'USD'
                      ? formatDisplayEstimateFiat(
                          maxCashoutDisplayValue
                            .dividedBy(tokenUsdPrices[(bonus as any).freeSpinCurrency])
                            .toFixed(
                              estimateFiatDecimalValues(tokens[ChainIdEnum.FIAT][(bonus as any)?.freeSpincurrency]),
                              BigNumber.ROUND_DOWN,
                            )
                            .toString(),
                          (bonus as any).freeSpinCurrency,
                        )
                      : isWagerBonusFiatReward(bonus)
                      ? formatDisplayEstimateFiat(
                          maxCashoutDisplayValue
                            .dividedBy(tokenUsdPrices[bonus.bonusAmount.token.code])
                            .toFixed(
                              estimateFiatDecimalValues(tokens[ChainIdEnum.FIAT][bonus.bonusAmount.token.code]),
                              BigNumber.ROUND_DOWN,
                            )
                            .toString(),

                          bonus.bonusAmount.token.code,
                        )
                      : ''}
                  </StyledFiatEstimated>
                </StyledMaxCashoutValue>
              </span>
            </Text>
          </RowBetween>
        )}

        <RowBetween>
          <Flex>
            <Text fontSize="14px" fontWeight="300" color="textSubtle">
              <Trans>Active Duration</Trans>
            </Text>
            <Box
              data-tooltip-id="common-tip-bottom"
              data-tooltip-content={t('The time window to activate your bonus')}
              ml={2}
              height="16px"
            >
              <Icons.InfoCircleIcon width="16px" fill={colors.textDisable} />
            </Box>
          </Flex>
          <Text fontSize="14px" fontWeight="600" color="textTertiary">
            {t(
              buildPluralizeText('{{activationDuration}} {{day}}', [
                { number: bonus.activeDurationInDays, key: 'day', word: ['day', 'days'] },
              ]),
              { activationDuration: bonus.activeDurationInDays },
            )}
          </Text>
        </RowBetween>
      </Box>
    </BonusDetailStepCard>
  )
}

const StyledFiatEstimated = styled(Text).attrs({ as: 'span' })`
  color: ${({ theme: { colors } }) => colors.textDisable};
  font-size: inherit;
  font-weight: inherit;
`

const StyledMaxCashoutValue = styled.span<{ $isMultiplierBonusValue }>`
  ${({ $isMultiplierBonusValue }) =>
    $isMultiplierBonusValue &&
    `color:  ${colors.textDisable};
     font-size: inherit;
     font-weight: inherit;`}
`

export default ActivationInfoSection
